/**
 * The brand colour, in one place — V8-03 §1.
 *
 * ## Why this exists
 *
 * The primary colour already flowed from `--accent` for everything rendered by
 * React, which is most of the app and the easy part. Five things could not read
 * a CSS variable and carried the hex themselves:
 *
 *   - `web/index.html`'s `theme-color` meta
 *   - the PWA manifest's `theme_color` in `vite.config.ts`
 *   - `web/public/favicon.svg`
 *   - the email template's wordmark and button (server-side, no CSS at all)
 *
 * So "flip the theme" meant finding all five by hand and hoping. This module is
 * the single source they all derive from, and `themeTokens.test.ts` fails if
 * the literal reappears anywhere else.
 *
 * ## What is *not* here
 *
 * Semantic colour. V8-03 §1 is explicit: success/done stays green, warnings
 * amber, danger red, priority spines and status categories unchanged — "task-
 * state meaning is not theme". `done` happens to have been the same green as
 * the old brand, which is exactly why it is worth saying: it stays green, and
 * a status colour that tracked the brand would have made every completed task
 * change meaning when marketing changed its mind.
 */
/** Primary action colour — buttons, active nav, links, selection. */
export const BRAND_PRIMARY = '#2563eb';
/** Hover and active steps, from the same blue ramp. */
export const BRAND_PRIMARY_HOVER = '#1d4ed8';
export const BRAND_PRIMARY_ACTIVE = '#1e40af';
/**
 * The dark-mode primary.
 *
 * A lighter step, because `#2563eb` on the dark surfaces does not carry enough
 * contrast for text or for a focus ring to be visible.
 */
export const BRAND_PRIMARY_DARK = '#3b82f6';
/**
 * What the browser paints around the app: address bar, task switcher, splash.
 *
 * The same value as the primary rather than a separate choice — a title bar in
 * a different blue from the buttons reads as two applications.
 */
export const BRAND_THEME_COLOR = BRAND_PRIMARY;
/**
 * The green that means *done*, not the green that used to mean *VirtueTasks*.
 *
 * These were the same hex until V8-03, which is exactly why this constant
 * exists: with one literal serving two unrelated jobs, "change the brand
 * colour" and "change what a completed task looks like" were the same edit,
 * and telling them apart afterwards meant reading every call site.
 *
 * V8-03 §1 keeps this green. Status categories, priority spines, chart series
 * and success states are meaning rather than branding, and a completed task
 * must not change colour because marketing did.
 */
export const SEMANTIC_SUCCESS = '#2F6F4E';
//# sourceMappingURL=brand.js.map